import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Field,
  H1,
  Input,
  Select,
  Table
} from '@itds/elements/index.js';
import React, { useEffect, useMemo, useState } from 'react';

import Api from '@carrier/utils/Api';
import PropTypes from 'prop-types';
import { TABLE_RATE_LETTERS } from '@itds/Constants';

const Edit = ({ tableRate, onClose, onSave, show }) => {
  const [data, setData] = useState({
    id: null,
    carrier_id: null,
    data_version_id: null,
    name: ''
  });

  const [autoSet, setAutoSet] = useState({
    rate_per_table: 25,
    start_at_table: 'A'
  });

  const [errors, setErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);

  const availableLetters = useMemo(() => {
    return TABLE_RATE_LETTERS.map(letter => ({
      letter,
      is_enabled: data[`table_${letter}`],
      min_age: data[`table_${letter}_min_age`],
      max_age: data[`table_${letter}_max_age`],
      percent: data[`table_${letter}_percent`]
    }));
  }, [data]);

  useEffect(() => {
    const new_table_rate = tableRate || {};
    const newData = {
      id: new_table_rate.id,
      carrier_id: new_table_rate.carrier_id,
      data_version_id: new_table_rate.data_version_id,
      name: new_table_rate.name || ''
    };

    const letters = 'ABCDEFGHIJKLMNOPQRSTUV'.split('');

    letters.forEach(letter => {
      newData[`table_${letter}`] = new_table_rate[`table_${letter}`] || false;
      newData[`table_${letter}_percent`] = new_table_rate[`table_${letter}_percent`] || '';
      newData[`table_${letter}_min_age`] = new_table_rate[`table_${letter}_min_age`] || '';
      newData[`table_${letter}_max_age`] = new_table_rate[`table_${letter}_max_age`] || '';
    });

    setData(newData);
    setErrors([]);
  }, [tableRate]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleInputAutoValues = (name, e) => {
    setAutoSet({ ...autoSet, [name]: e.target.value });
  };

  const handleCheckboxChange = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };

  const handleSave = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }

    TABLE_RATE_LETTERS.forEach(letter => {
      if (data[`table_${letter}`] && !data[`table_${letter}_percent`]) {
        errors.push(`table_${letter}_percent`);
      }
    });

    setErrors(errors);

    if (!errors.length) {
      const { id, ...payload } = data;

      Api.saveTableRate(id, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  const handlePopulateRates = rate => {
    const findLetterIndex = availableLetters.findIndex(
      row => row.letter === autoSet.start_at_table
    );
    const lettersToPopulate = availableLetters.filter((_, idx) => idx >= findLetterIndex);

    let newData = data;

    let incremented_rate = parseFloat(rate, 10);

    lettersToPopulate.map(row => {
      newData = {
        ...newData,
        [`table_${row.letter}_percent`]: incremented_rate
      };
      incremented_rate += parseFloat(rate, 10); // increment row's rate by rate_per_table
    });

    setData(newData);
  };

  const handleToggleAll = checkboxValue => {
    let newData = data;

    availableLetters.map(row => {
      newData = {
        ...newData,
        [`table_${row.letter}`]: checkboxValue
      };
    });

    setData(newData);
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        <Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create Table Rate'}</Button>
      }
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Table Rate' : 'New Table Rate'}
      wide={true}
    >
      {save_error ? (
        <Alert type='error'>An error occurred while trying to save the table rate.</Alert>
      ) : null}

      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          required={true}
          value={data.name}
        />
      </Field>

      <Field label='Rate Per Table (%)'>
        <Input
          min={1}
          onChange={handleInputAutoValues.bind(null, 'rate_per_table')}
          type='number'
          value={autoSet.rate_per_table}
        />
      </Field>

      <Field label='Start at Table'>
        <Select
          onChange={handleInputAutoValues.bind(null, 'start_at_table')}
          options={availableLetters.map(it => ({ label: it.letter, value: it.letter }))}
          value={autoSet.start_at_table}
        />
      </Field>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        <div>
          <H1>Rates</H1>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
          <Button
            onClick={handlePopulateRates.bind(null, autoSet.rate_per_table)}
            small={true}
            style={{ marginRight: 2, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          >
            {'Auto-Populate Fields'}
          </Button>
          <Button
            color='GRAY'
            onClick={handlePopulateRates.bind(null, 0)}
            small={true}
            style={{ marginRight: 2, borderRadius: 0 }}
          >
            {'Zero Rates'}
          </Button>
          <Button
            color='RED'
            onClick={handleToggleAll.bind(null, false)}
            small={true}
            style={{ marginRight: 2, borderRadius: 0 }}
          >
            {'All Off'}
          </Button>
          <Button
            color='SECONDARY'
            onClick={handleToggleAll.bind(null, true)}
            small={true}
            style={{ marginRight: 2, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            {'All On'}
          </Button>
        </div>
      </div>

      <Table
        columns={[
          {
            id: 'letter',
            label: 'Table'
          },
          {
            id: 'is_enabled',
            label: 'Enabled',
            render: row => {
              return (
                <Checkbox
                  checked={row.is_enabled}
                  onChange={handleCheckboxChange.bind(null, `table_${row.letter}`)}
                />
              );
            },
            styles: { display: 'flex', justifyContent: 'center' }
          },
          {
            id: 'min_age',
            label: 'Min Age',
            render: row => {
              return (
                <Input
                  onChange={handleInputChange.bind(null, `table_${row.letter}_min_age`)}
                  readOnly={!data[`table_${row.letter}`]}
                  type='number'
                  value={row.min_age}
                />
              );
            }
          },
          {
            id: 'max_age',
            label: 'Max Age',
            render: row => {
              return (
                <Input
                  onChange={handleInputChange.bind(null, `table_${row.letter}_max_age`)}
                  readOnly={!data[`table_${row.letter}`]}
                  type='number'
                  value={row.max_age}
                />
              );
            }
          },
          {
            id: 'percent',
            label: 'Rate (%)',
            render: row => {
              return (
                <Input
                  invalid={errors.includes(`table_${row.letter}_percent`)}
                  onChange={handleInputChange.bind(null, `table_${row.letter}_percent`)}
                  readOnly={!data[`table_${row.letter}`]}
                  required={true}
                  type='number'
                  value={row.percent}
                />
              );
            }
          }
        ]}
        data={availableLetters}
      />
    </Drawer>
  );
};

Edit.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  show: PropTypes.bool,
  tableRate: PropTypes.object
};

export default Edit;
